import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Container } from "@magnetic/container";
import { Spinner } from "@magnetic/spinner";

import { ErrorBoundary } from "src/components/errorBoundary";
import { AppHeader } from "src/core/appHeader/appHeader";
import { PATHS } from "src/core/constants";
import { useUrlHashState } from "src/hooks/useUrlState";
import { PageLayout } from "src/layouts";
import { CaptiveScreen } from "src/layouts/CaptiveLayout/CaptiveScreen";

import { DataProvider } from "./provider/dataProvider";
import { CrossGeoAddOrg } from "./crossGeoAddOrg";
import { Login } from "./login";
import { LoginContext } from "./loginContext";
import { Navbar } from "./navbar";
import { TimezoneProvider } from "./timeZoneProvider";
import { WelcomeScreen } from "./welcomeScreen";

const DevLogin = lazy(() => import("src/core/devLogin"));
const C3Login = lazy(() => import("src/core/c3Login"));

export const Shell = ({
  children
}: PropsWithChildren): React.ReactElement | null => {
  const isDevMode: boolean = window.cnc?.settings?.get("dev");
  const { session, configLimits } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const clToken = useUrlHashState("cl_token");
  const isC3Login: boolean =
    location.pathname === `/${PATHS.LOGIN}` && !!clToken;

  const isCrossGeoAddOrg = location.pathname === `/${PATHS.ADD_ORG}`;

  const navigateTo = useCallback(
    (
      path: string,
      queryString: string = "",
      options?: {
        replace?: boolean;
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        state?: any;
      }
    ) => {
      if (window.location.pathname !== path) {
        navigate(`${path}${queryString}`, options);
      }
    },
    [navigate]
  );

  /**
   * wrapping the navigate calls in useEffect
   * as to not call setState while rendering
   */
  useEffect(() => {
    const PATH_LOGIN = `/${PATHS.LOGIN}`;
    const PATH_LOGIN_DEV = `/${PATHS.DEV_LOGIN}`;
    const PATH_SIGN_UP = "/signup";
    if (!isC3Login) {
      if (!session) {
        navigateTo(
          `${isDevMode ? PATH_LOGIN_DEV : PATH_LOGIN}`,
          location.search,
          {
            state: { location }
          }
        );
      }

      if (session?.newUser || session?.requireSignUp) {
        if (!isCrossGeoAddOrg) {
          navigateTo(PATH_SIGN_UP);
        }
      }
    }
  }, [isDevMode, location, navigateTo, session, isC3Login, isCrossGeoAddOrg]);

  if (isC3Login) {
    return (
      <Suspense fallback={<Spinner />}>
        <C3Login />
      </Suspense>
    );
  }

  /**
   * IMPORTANT NOTE:
   * Unauthenticated users reach this login path from the default UI entry point
   * before the session API returns the user's target uiVersion. Changes here
   * must remain backward-compatible with the currently deployed backend and the
   * version-switch behavior in useVersionUpdate. If login bootstrap changes
   * require backend support then ensure the backend supports both old and new
   * changes.
   *
   * See docs/design/Tortuga-Per-Cell-UI-Versioning.md for more details.
   */
  if (!session) {
    return isDevMode ? (
      <ErrorBoundary>
        <Suspense fallback={<Spinner />}>
          <DevLogin />
        </Suspense>
      </ErrorBoundary>
    ) : (
      <Login />
    );
  }

  if (isCrossGeoAddOrg && session) {
    return <CrossGeoAddOrg />;
  }

  if (session?.newUser || session?.requireSignUp) {
    return <WelcomeScreen />;
  }

  return (
    <TimezoneProvider>
      <DataProvider configLimits={configLimits}>
        <CaptiveScreen>
          <PageLayout
            banner={<AppHeader />}
            sideNav={<Navbar />}
            content={
              <Container>
                <ErrorBoundary
                  resetKey={location.pathname}
                  message="Unable to load this page. Please refresh to try again."
                >
                  {children}
                </ErrorBoundary>
              </Container>
            }
          />
        </CaptiveScreen>
      </DataProvider>
    </TimezoneProvider>
  );
};
